import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Badge,
    Button,
    ButtonBase,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grow,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    useMediaQuery,
    useTheme
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import AddIcon from '@material-ui/icons/Add';
import CheckIcon from '@material-ui/icons/Check';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import EditIcon from '@material-ui/icons/Edit';
import FolderIcon from '@material-ui/icons/Folder';
import HomeIcon from '@material-ui/icons/Home';
import PhotoIcon from '@material-ui/icons/Photo';
import PublishIcon from '@material-ui/icons/Publish';
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import jstring from '@easytech-international-sdn-bhd/jstring';
import {useSnackbar} from 'notistack';
import API_Service, {axiosURIencode} from '../../../../Services/API_Service';
import {req_gallery_group, req_gallery_type, req_gallery_type_seq, req_gallery_type_status} from '../../../../Helper/Constants';
import {EmptyState, SectionToolbar, SettingsSurface, StatusSwitch, StatusTabs} from './ui';

export const createPhotoTypeV2Styles = (theme) => ({
    addButton: {
        width: 40,
        height: 40,
        color: theme.palette.getContrastText(theme.palette.primary.main),
        backgroundColor: theme.palette.primary.main,
        boxShadow: '0 7px 16px rgba(0,0,0,.16)',
        transition: 'transform 140ms cubic-bezier(.34,1.3,.64,1)',
        '&:hover': {backgroundColor: theme.palette.primary.main, transform: 'translateY(-2px) scale(1.04)'}
    },
    publish: {
        minWidth: 148,
        borderRadius: 10,
        fontWeight: 750,
        transition: 'color 180ms ease, border-color 180ms ease, background-color 180ms ease, box-shadow 220ms ease'
    },
    publishSuccess: {
        '&&': {
            color: '#fff',
            borderColor: theme.palette.success ? theme.palette.success.main : '#2e7d32',
            backgroundColor: theme.palette.success ? theme.palette.success.main : '#2e7d32',
            boxShadow: '0 6px 16px rgba(46,125,50,.22)'
        },
        '&&.Mui-disabled': {
            color: '#fff',
            borderColor: theme.palette.success ? theme.palette.success.main : '#2e7d32',
            backgroundColor: theme.palette.success ? theme.palette.success.main : '#2e7d32',
            boxShadow: '0 6px 16px rgba(46,125,50,.22)',
            opacity: 1
        }
    },
    publishLoading: {
        '&&.Mui-disabled': {
            color: theme.palette.text.primary,
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.type === 'dark' ? 'rgba(254,242,0,.10)' : 'rgba(254,242,0,.08)',
            opacity: 1
        }
    },
    breadcrumb: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2,
        minHeight: 38,
        marginBottom: theme.spacing(1.5)
    },
    crumb: {
        minHeight: 32,
        padding: theme.spacing(0.5, 1),
        borderRadius: 8,
        color: theme.palette.text.secondary,
        fontSize: 12,
        fontWeight: 700,
        '&:hover': {color: theme.palette.text.primary, backgroundColor: theme.palette.action.hover},
        '&:focus-visible': {outline: `2px solid ${theme.palette.primary.main}`}
    },
    table: {minWidth: 840},
    head: {'& .MuiTableCell-root': {fontSize: 11, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase'}},
    row: {
        transition: 'background-color 180ms cubic-bezier(.2,0,0,1), box-shadow 220ms cubic-bezier(.2,0,0,1)',
        '&:hover': {backgroundColor: theme.palette.action.hover},
        '& .MuiTableCell-root': {borderBottomColor: theme.palette.divider}
    },
    rowSettled: {animation: '$rowSettle 520ms cubic-bezier(.2,0,0,1) both'},
    groupRow: {cursor: 'pointer'},
    dragging: {
        display: 'table',
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0 18px 38px rgba(0,0,0,.2)',
        '& .MuiTableCell-root:first-child': {borderLeft: `3px solid ${theme.palette.primary.main}`}
    },
    dragHandle: {display: 'inline-flex', color: theme.palette.text.secondary, cursor: 'grab', '&:active': {cursor: 'grabbing'}},
    nameCell: {display: 'flex', alignItems: 'center', gap: theme.spacing(1), fontWeight: 700},
    folder: {color: theme.palette.primary.main},
    kindChip: {
        minWidth: 60,
        color: '#fff',
        fontWeight: 800,
        '& .MuiChip-label': {paddingLeft: 16, paddingRight: 16}
    },
    kindGroup: {color: '#fff', backgroundColor: '#32CD32'},
    kindType: {color: '#fff', backgroundColor: '#FF0000'},
    loader: {padding: theme.spacing(1, 2)},
    dialogPaper: {borderRadius: 16},
    dialogTitle: {fontWeight: 800},
    modeTabs: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 4,
        marginBottom: theme.spacing(2.5),
        padding: 4,
        borderRadius: 12,
        backgroundColor: theme.palette.action.hover
    },
    modeTab: {minHeight: 38, borderRadius: 9, color: theme.palette.text.secondary, fontSize: 13, fontWeight: 800},
    modeActive: {
        color: theme.palette.getContrastText(theme.palette.primary.main),
        backgroundColor: theme.palette.primary.main,
        boxShadow: '0 5px 14px rgba(0,0,0,.14)',
        '&.Mui-disabled': {
            color: theme.palette.getContrastText(theme.palette.primary.main),
            backgroundColor: theme.palette.primary.main,
            opacity: 1
        }
    },
    fields: {display: 'grid', gap: theme.spacing(2)},
    typeGrid: {display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: theme.spacing(2), [theme.breakpoints.down('xs')]: {gridTemplateColumns: '1fr'}},
    spanTwo: {gridColumn: '1 / -1'},
    field: {'& .MuiOutlinedInput-root': {borderRadius: 11}},
    qty: {justifyContent: 'space-between', margin: 0, padding: theme.spacing(0.5, 0)},
    qtyCheckbox: {
        color: theme.palette.text.secondary,
        '&.Mui-checked': {color: theme.palette.primary.main},
        '&.Mui-disabled.Mui-checked': {
            color: theme.palette.primary.main,
            opacity: 1
        }
    },
    '@keyframes rowSettle': {
        '0%': {
            backgroundColor: theme.palette.type === 'dark' ? 'rgba(254,242,0,.16)' : 'rgba(254,242,0,.22)',
            boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`
        },
        '100%': {
            backgroundColor: 'transparent',
            boxShadow: 'inset 0 0 0 rgba(0,0,0,0)'
        }
    }
});

const useStyles = makeStyles(createPhotoTypeV2Styles);

export const isPhotoTypeRoot = (value) => value === null;
const sameId = (left, right) => String(left) === String(right);

const searchItems = (items, query) => {
    const normalized = jstring(query);
    if (!normalized.isValid()) return items;
    const tokens = normalized.toString().split(' ');
    return items.filter((item) => tokens.every((token) => jstring(item.type_name).searchDeep(token)));
};

export default function PhotoTypeV2Section({axiosConfig, axiosPut, language}) {
    const classes = useStyles();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
    const {enqueueSnackbar} = useSnackbar();
    const [items, setItems] = useState(null);
    const [statusFilter, setStatusFilter] = useState(1);
    const [search, setSearch] = useState('');
    const [parentId, setParentId] = useState(null);
    const [breadcrumb, setBreadcrumb] = useState([]);
    const [moveCount, setMoveCount] = useState(0);
    const [settledRowId, setSettledRowId] = useState(null);
    const [publishStatus, setPublishStatus] = useState('idle');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [mode, setMode] = useState('group');
    const [editId, setEditId] = useState(null);
    const [groupName, setGroupName] = useState('');
    const [typeName, setTypeName] = useState('');
    const [commission, setCommission] = useState(0);
    const [minPhoto, setMinPhoto] = useState(0);
    const [maxPhoto, setMaxPhoto] = useState(5);
    const [qtyNeeded, setQtyNeeded] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        setItems(null);
        API_Service.get(req_gallery_type, axiosConfig).then(({data}) => {
            if (parseInt(data.result) === 1) setItems(data.extraData || []);
            else {
                setItems([]);
                enqueueSnackbar(data.message || language.operation_fail, {variant: 'error'});
            }
        }).catch(() => {
            setItems([]);
            enqueueSnackbar(language.operation_fail || 'Network error', {variant: 'error'});
        });
    }, [axiosConfig, enqueueSnackbar, language.operation_fail]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (settledRowId === null) return undefined;
        const timer = setTimeout(() => setSettledRowId(null), 520);
        return () => clearTimeout(timer);
    }, [settledRowId]);

    useEffect(() => {
        if (publishStatus !== 'success') return undefined;
        const timer = setTimeout(() => setPublishStatus('idle'), 1100);
        return () => clearTimeout(timer);
    }, [publishStatus]);

    const statusItems = useMemo(() => (items || []).filter((item) => parseInt(item.type_status) === parseInt(statusFilter)), [items, statusFilter]);
    const levelItems = useMemo(() => statusItems.filter((item) => parentId === null ? isPhotoTypeRoot(item.type_parent_id) : sameId(item.type_parent_id, parentId)), [parentId, statusItems]);
    const displayed = useMemo(() => searchItems(levelItems, search), [levelItems, search]);

    const changeStatusFilter = (value) => {
        setStatusFilter(value);
        setParentId(null);
        setBreadcrumb([]);
        setSearch('');
        setMoveCount(0);
        setSettledRowId(null);
        setPublishStatus('idle');
        setPage(0);
    };

    const enterGroup = (item) => {
        if (String(item.is_leaf) !== '0') return;
        setBreadcrumb((current) => [...current, item]);
        setParentId(item.type_id);
        setSearch('');
        setMoveCount(0);
        setSettledRowId(null);
        setPublishStatus('idle');
        setPage(0);
    };

    const goToCrumb = (index) => {
        if (index < 0) {
            setParentId(null);
            setBreadcrumb([]);
        } else {
            setParentId(breadcrumb[index].type_id);
            setBreadcrumb(breadcrumb.slice(0, index + 1));
        }
        setSearch('');
        setMoveCount(0);
        setSettledRowId(null);
        setPublishStatus('idle');
        setPage(0);
    };

    const onDragEnd = ({source, destination}) => {
        if (!destination || destination.index === source.index || search) return;
        const sourceIndex = page * rowsPerPage + source.index;
        const destinationIndex = page * rowsPerPage + destination.index;
        const reordered = [...levelItems];
        const [moved] = reordered.splice(sourceIndex, 1);
        reordered.splice(destinationIndex, 0, moved);
        const levelIds = new Set(reordered.map((item) => String(item.type_id)));
        setItems([...(items || []).filter((item) => !levelIds.has(String(item.type_id))), ...reordered]);
        setSettledRowId(String(moved.type_id));
        setPublishStatus('idle');
        setMoveCount((count) => count + 1);
    };

    const publish = () => {
        if (!moveCount || publishStatus === 'loading') return;
        setPublishStatus('loading');
        const formdata = new FormData();
        formdata.append('array', JSON.stringify(levelItems.map((item, index) => ({type_id: item.type_id, sequence_no: index + 1}))));
        API_Service.post(req_gallery_type_seq, formdata, axiosConfig).then(({data}) => {
            if (parseInt(data.result) === 1) {
                enqueueSnackbar(data.message || language.operation_success, {variant: 'success'});
                setMoveCount(0);
                setPublishStatus('success');
            } else {
                setPublishStatus('idle');
                enqueueSnackbar(data.message || language.operation_fail, {variant: 'error'});
            }
        }).catch(() => {
            setPublishStatus('idle');
            enqueueSnackbar(language.operation_fail || 'Network error', {variant: 'error'});
        });
    };

    const [togglingId, setTogglingId] = useState(null);

    const toggleStatus = (item) => {
        setTogglingId(item.type_id);
        API_Service.put(req_gallery_type_status, axiosURIencode({type_id: item.type_id}), axiosPut).then(({data}) => {
            if (parseInt(data.result) === 1) {
                enqueueSnackbar(data.message || language.operation_success, {variant: 'success'});
                load();
            } else enqueueSnackbar(data.message || language.operation_fail, {variant: 'error'});
        }).catch(() => enqueueSnackbar(language.operation_fail || 'Network error', {variant: 'error'}))
            .finally(() => setTogglingId(null));
    };

    const resetDialog = () => {
        setDialogOpen(false);
        setMode('group');
        setEditId(null);
        setGroupName('');
        setTypeName('');
        setCommission(0);
        setMinPhoto(0);
        setMaxPhoto(5);
        setQtyNeeded(false);
    };

    const openAdd = () => {
        resetDialog();
        setDialogOpen(true);
    };

    const openEdit = (item) => {
        setEditId(item.type_id);
        if (String(item.is_leaf) === '1') {
            setMode('type');
            setTypeName(item.type_name || '');
            setCommission(item.type_commission || 0);
            setMinPhoto(item.type_min == null ? 0 : item.type_min);
            setMaxPhoto(item.type_max == null ? 5 : item.type_max);
            setQtyNeeded(String(item.type_qty) === '1');
        } else {
            setMode('group');
            setGroupName(item.type_name || '');
        }
        setDialogOpen(true);
    };

    const saveGroup = () => {
        if (!groupName.trim()) {
            enqueueSnackbar(language.name_required || 'Description cannot be blank', {variant: 'error'});
            return;
        }
        setSaving(true);
        const request = editId
            ? API_Service.put(req_gallery_group, axiosURIencode({type_id: editId, type_name: groupName}), axiosPut)
            : (() => {
                const formdata = new FormData();
                formdata.append('type_name', groupName);
                formdata.append('type_parent_id', parentId || '');
                return API_Service.post(req_gallery_group, formdata, axiosConfig);
            })();
        handleSaveRequest(request);
    };

    const saveType = () => {
        if (!typeName.trim()) {
            enqueueSnackbar(language.name_required || 'Description cannot be blank', {variant: 'error'});
            return;
        }
        if (!maxPhoto || Number(maxPhoto) < 1) {
            enqueueSnackbar(language.max_photo_error || 'Max Photo must be greater than 0.', {variant: 'error'});
            return;
        }
        if (Number(minPhoto) > Number(maxPhoto)) {
            enqueueSnackbar(language.min_photo_error || 'Min Photo cannot be greater than Max Photo.', {variant: 'error'});
            return;
        }
        setSaving(true);
        const request = editId
            ? API_Service.put(req_gallery_type, axiosURIencode({
                type_id: editId,
                type_name: typeName,
                type_commission: commission,
                type_min: minPhoto,
                type_max: maxPhoto,
                type_qty: qtyNeeded
            }), axiosPut)
            : (() => {
                const formdata = new FormData();
                formdata.append('type_name', typeName);
                formdata.append('type_commission', commission);
                formdata.append('type_min', Number(minPhoto));
                formdata.append('type_max', Number(maxPhoto));
                formdata.append('type_qty', qtyNeeded);
                formdata.append('type_parent_id', parentId || '');
                formdata.append('version', 2);
                return API_Service.post(req_gallery_type, formdata, axiosConfig);
            })();
        handleSaveRequest(request);
    };

    function handleSaveRequest(request) {
        request.then(({data}) => {
            setSaving(false);
            if (parseInt(data.result) === 1) {
                enqueueSnackbar(data.message || language.operation_success, {variant: 'success'});
                resetDialog();
                load();
            } else enqueueSnackbar(data.message || language.operation_fail, {variant: 'error'});
        }).catch(() => {
            setSaving(false);
            enqueueSnackbar(language.operation_fail || 'Network error', {variant: 'error'});
        });
    }

    const pageItems = displayed.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <React.Fragment>
            <div className={classes.breadcrumb} aria-label={language.breadcrumb || 'Breadcrumb'}>
                <ButtonBase className={classes.crumb} onClick={() => goToCrumb(-1)}><HomeIcon fontSize="small"/>&nbsp;{language.root || 'Root'}</ButtonBase>
                {breadcrumb.map((item, index) => (
                    <React.Fragment key={item.type_id}>
                        <ChevronRightIcon fontSize="small" color="disabled"/>
                        <ButtonBase className={classes.crumb} onClick={() => goToCrumb(index)}>{item.type_name}</ButtonBase>
                    </React.Fragment>
                ))}
            </div>
            <SectionToolbar search={search} onSearch={(value) => { setSearch(value); setPage(0); }} placeholder={language.search}>
                <StatusTabs value={statusFilter} onChange={changeStatusFilter} activeLabel={language.active} inactiveLabel={language.inactive}/>
                <Badge badgeContent={moveCount} color="error" overlap="rectangular">
                    <Button
                        className={`${classes.publish} ${publishStatus === 'loading' ? classes.publishLoading : ''} ${publishStatus === 'success' ? classes.publishSuccess : ''}`}
                        variant="outlined"
                        disabled={moveCount === 0 || publishStatus === 'loading'}
                        startIcon={publishStatus === 'loading' ? <CircularProgress size={16} color="inherit"/> : publishStatus === 'success' ? <CheckIcon/> : <PublishIcon/>}
                        onClick={publish}
                        aria-busy={publishStatus === 'loading'}
                        aria-live="polite"
                    >
                        {publishStatus === 'success' ? (language.published || language.saved || language.operation_success) : (language.publish_sequence || language.update)}
                    </Button>
                </Badge>
                <Tooltip title={language.add}>
                    <IconButton className={classes.addButton} onClick={openAdd} aria-label={language.add}><AddIcon/></IconButton>
                </Tooltip>
            </SectionToolbar>
            <SettingsSurface>
                {items === null ? (
                    <div className={classes.loader}>{[0, 1, 2, 3].map((row) => <Skeleton key={row} animation="wave" height={58}/>)}</div>
                ) : displayed.length === 0 ? <EmptyState label={language.no_data}/> : (
                    <React.Fragment>
                        <TableContainer>
                            <Table className={classes.table} size="small" aria-label={language.photo_type}>
                                <TableHead className={classes.head}>
                                    <TableRow>
                                        <TableCell width={44}/>
                                        <TableCell>{language.description || 'Description'}</TableCell>
                                        <TableCell align="right">{language.commission || 'Commission'}</TableCell>
                                        <TableCell align="right">{language.min_photo || 'Min Photo'}</TableCell>
                                        <TableCell align="right">{language.max_photo || 'Max Photo'}</TableCell>
                                        <TableCell>{language.group_type || 'Group / Type'}</TableCell>
                                        <TableCell align="center">{language.qty_needed || 'QTY Needed'}</TableCell>
                                        <TableCell align="right">{language.action}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <DragDropContext onDragEnd={onDragEnd}>
                                    <Droppable droppableId={`photo-type-v2-${statusFilter}-${parentId || 'root'}`} direction="vertical">
                                        {(provided) => (
                                            <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                                                {pageItems.map((item, index) => {
                                                    const isGroup = String(item.is_leaf) === '0';
                                                    const active = parseInt(item.type_status) === 1;
                                                    return (
                                                        <Draggable key={String(item.type_id)} draggableId={String(item.type_id)} index={index} isDragDisabled={Boolean(search) || publishStatus === 'loading'}>
                                                            {(dragProvided, snapshot) => (
                                                                <TableRow
                                                                    ref={dragProvided.innerRef}
                                                                    {...dragProvided.draggableProps}
                                                                    className={`${classes.row} ${isGroup ? classes.groupRow : ''} ${snapshot.isDragging ? classes.dragging : ''} ${sameId(item.type_id, settledRowId) ? classes.rowSettled : ''}`}
                                                                    style={dragProvided.draggableProps.style}
                                                                    onClick={() => isGroup && enterGroup(item)}
                                                                >
                                                                    <TableCell>
                                                                        <span className={classes.dragHandle} {...dragProvided.dragHandleProps} tabIndex={search || publishStatus === 'loading' ? -1 : 0} aria-label={language.reorder || 'Reorder'}>
                                                                            <DragIndicatorIcon fontSize="small"/>
                                                                        </span>
                                                                    </TableCell>
                                                                    <TableCell><span className={classes.nameCell}>{isGroup ? <FolderIcon className={classes.folder} fontSize="small"/> : <PhotoIcon fontSize="small"/>}{item.type_name}{isGroup ? <ChevronRightIcon fontSize="small"/> : null}</span></TableCell>
                                                                    <TableCell align="right">{isGroup ? '—' : item.type_commission}</TableCell>
                                                                    <TableCell align="right">{isGroup ? '—' : item.type_min}</TableCell>
                                                                    <TableCell align="right">{isGroup ? '—' : item.type_max}</TableCell>
                                                                    <TableCell><Chip className={`${classes.kindChip} ${isGroup ? classes.kindGroup : classes.kindType}`} size="small" label={isGroup ? (language.group || 'Group') : (language.type || 'Type')}/></TableCell>
                                                                    <TableCell align="center">{isGroup ? '—' : <Checkbox className={classes.qtyCheckbox} checked={String(item.type_qty) === '1'} disabled size="small"/>}</TableCell>
                                                                    <TableCell align="right">
                                                                        <Tooltip title={language.edit}>
                                                                            <IconButton size="small" onClick={(event) => { event.stopPropagation(); openEdit(item); }}><EditIcon fontSize="small"/></IconButton>
                                                                        </Tooltip>
                                                                        <StatusSwitch active={active} pending={String(togglingId) === String(item.type_id)} name={item.type_name} language={language} onToggle={() => toggleStatus(item)}/>
                                                                    </TableCell>
                                                                </TableRow>
                                                            )}
                                                        </Draggable>
                                                    );
                                                })}
                                                {provided.placeholder}
                                            </TableBody>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={displayed.length}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={[100, 140, 165, 200]}
                            onPageChange={(event, value) => setPage(value)}
                            onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
                        />
                    </React.Fragment>
                )}
            </SettingsSurface>
            <Dialog open={dialogOpen} onClose={() => { if (!saving) resetDialog(); }} fullScreen={fullScreen} fullWidth maxWidth="sm" TransitionComponent={Grow} PaperProps={{className: classes.dialogPaper}}>
                <DialogTitle className={classes.dialogTitle}>{editId ? language.edit : language.add} · {mode === 'group' ? (language.gallery_group || 'Gallery Group') : language.photo_type}</DialogTitle>
                <DialogContent>
                    <div className={classes.modeTabs} role="tablist" aria-label={language.group_type || 'Group or type'}>
                        {['group', 'type'].map((option) => (
                            <ButtonBase
                                key={option}
                                className={`${classes.modeTab} ${mode === option ? classes.modeActive : ''}`}
                                disabled={Boolean(editId)}
                                role="tab"
                                aria-selected={mode === option}
                                onClick={() => setMode(option)}
                            >
                                {option === 'group' ? (language.gallery_group || 'Gallery Group') : language.photo_type}
                            </ButtonBase>
                        ))}
                    </div>
                    {mode === 'group' ? (
                        <div className={classes.fields}>
                            {editId ? <TextField className={classes.field} fullWidth disabled variant="outlined" label={language.code || 'Code'} value={`PG${editId}`}/> : null}
                            <TextField className={classes.field} autoFocus fullWidth required variant="outlined" label={language.description || 'Description'} value={groupName} onChange={(event) => setGroupName(event.target.value)} /* inputProps={{maxLength: 25}} */ />
                        </div>
                    ) : (
                        <div className={classes.typeGrid}>
                            {editId ? <TextField className={`${classes.field} ${classes.spanTwo}`} fullWidth disabled variant="outlined" label={language.code || 'Code'} value={`PT${editId}`}/> : null}
                            <TextField className={`${classes.field} ${classes.spanTwo}`} autoFocus fullWidth required variant="outlined" label={language.description || 'Description'} value={typeName} onChange={(event) => setTypeName(event.target.value)} /* inputProps={{maxLength: 25}} */ />
                            <TextField className={`${classes.field} ${classes.spanTwo}`} fullWidth variant="outlined" type="number" label={language.commission || 'Commission'} value={commission} onChange={(event) => { if (/^\d*$/.test(event.target.value)) setCommission(event.target.value); }} inputProps={{min: 0}}/>
                            <TextField className={classes.field} fullWidth variant="outlined" type="number" label={language.min_photo || 'Min Photo'} value={minPhoto} onChange={(event) => { const value = event.target.value; if (value === '' || /^\d+$/.test(value)) setMinPhoto(value === '' ? '' : parseInt(value, 10)); }} onBlur={() => { if (minPhoto === '' || Number(minPhoto) < 0) setMinPhoto(0); }} inputProps={{min: 0}}/>
                            <TextField className={classes.field} fullWidth variant="outlined" type="number" label={language.max_photo || 'Max Photo'} value={maxPhoto} onChange={(event) => { const value = event.target.value; if (value === '' || /^\d+$/.test(value)) setMaxPhoto(value === '' ? '' : parseInt(value, 10)); }} onBlur={() => { if (maxPhoto === '' || Number(maxPhoto) < 1) setMaxPhoto(5); }} inputProps={{min: 1}}/>
                            <FormControlLabel className={`${classes.qty} ${classes.spanTwo}`} labelPlacement="start" label={language.qty_needed || 'QTY Needed'} control={<Checkbox className={classes.qtyCheckbox} checked={qtyNeeded} onChange={(event) => setQtyNeeded(event.target.checked)}/>}/>
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button disabled={saving} onClick={resetDialog}>{language.cancel}</Button>
                    <Button variant="contained" color="primary" disabled={saving || (mode === 'group' ? !groupName.trim() : !typeName.trim())} onClick={mode === 'group' ? saveGroup : saveType}>
                        {saving ? <CircularProgress size={18}/> : editId ? language.update : language.add}
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
